import React, { useState, useEffect } from "react";
import {
  Modal,
  Fade,
  Box,
  Typography,
  IconButton,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  List,
  ListItem,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const AIDropoffModal = ({
  open,
  handleClose,
  accessToken,
  API_URL,
  setOpenSnackbar,
  setSnackbarMessage,
  setSnackbarSeverity,
}) => {
  // --- States ---
  const [activeStep, setActiveStep] = useState(1); // 1: List View, 2: Form View
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0], // Default aaj ki date
  );
  const [predictions, setPredictions] = useState([]);
  const [aiReasons, setAiReasons] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(false);

  const [aiForm, setAiForm] = useState({
    service_status: "",
    reason: "",
    Remark: "",
    in_folloup: "",
  });

  // --- Modal open hone par APIs Call ---
  useEffect(() => {
    if (open) {
      fetchReasons();
      fetchPredictions(selectedDate);
      setActiveStep(1);
    }
  }, [open]);

  // 1. GET API: Reasons ke liye
  const fetchReasons = async () => {
    try {
      const res = await fetch(`${API_URL}/web/service_drop_reasons/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      setAiReasons(data || []);
    } catch (error) {
      console.error("Error fetching reasons:", error);
    }
  };

  // 2. POST API: Date bhej kar predictions list lana
  const fetchPredictions = async (dateStr) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/web/predict-dropoff/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ date: dateStr }),
      });
      const data = await res.json();
      // Assume array return hota hai
      setPredictions(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error("Error fetching predictions:", error);
    } finally {
      setLoading(false);
    }
  };

  // 3. POST API: Reason submit karna
  const handleAiSubmit = async (event) => {
    event.preventDefault();

    const requestData = {
      ptn_id: parseInt(selectedPatient.ptn_id),
      dtl_id: parseInt(selectedPatient.dtl_id),
      reason: parseInt(aiForm.reason),
      Remark: aiForm.Remark,
      service_status: parseInt(aiForm.service_status),
    };

    // Agar status 2 (Follow-up) hai, tabhi date add karo
    if (requestData.service_status === 2) {
      requestData.in_folloup = aiForm.in_folloup;
    }

    try {
      const response = await fetch(`${API_URL}/web/submit_dropoff_reason/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      setOpenSnackbar(true);
      setSnackbarMessage("Feedback submitted successfully!");
      setSnackbarSeverity("success");

      // Reset karke wapas list par bhejo
      setAiForm({ service_status: "", reason: "", Remark: "", in_folloup: "" });
      setSelectedPatient(null);
      setActiveStep(1);

      // List ko refresh karo taaki updated data aaye
      fetchPredictions(selectedDate);
    } catch (error) {
      console.error("Submit error:", error);
      setOpenSnackbar(true);
      setSnackbarMessage("Failed to submit details.");
      setSnackbarSeverity("error");
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    fetchPredictions(newDate);
  };

  const handleActionClick = (patient) => {
    setSelectedPatient(patient);
    setActiveStep(2); // Form view dikhao
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setAiForm((prev) => ({ ...prev, [name]: value }));
  };

  const closeAndReset = () => {
    handleClose();
    setTimeout(() => {
      setActiveStep(1);
      setSelectedPatient(null);
      setAiForm({ service_status: "", reason: "", Remark: "", in_folloup: "" });
    }, 300);
  };

  return (
    <Modal open={open} onClose={closeAndReset} closeAfterTransition>
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 550 },
            maxHeight: "85vh",
            bgcolor: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 10px 40px rgba(168, 85, 247, 0.3)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: "1px solid rgba(168, 85, 247, 0.4)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #1e1e2f 0%, #2d2b42 100%)",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "3px solid #a855f7",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "#fff",
              }}
            >
              {activeStep === 2 ? (
                <IconButton
                  size="small"
                  onClick={() => setActiveStep(1)}
                  sx={{ color: "#fff", mr: 1 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              ) : (
                <PsychologyIcon sx={{ color: "#a855f7", fontSize: 28 }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {activeStep === 1 ? "AI Drop-off Predictions" : "Submit Reason"}
              </Typography>
            </Box>
            <IconButton
              onClick={closeAndReset}
              sx={{ color: "rgba(255,255,255,0.7)" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, overflowY: "auto", flexGrow: 1 }}>
            {/* STEP 1: Date Picker + List of Patients */}
            {activeStep === 1 && (
              <Box>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Select Date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Chip
                      icon={<AutoAwesomeIcon sx={{ fontSize: "16px" }} />}
                      label={`${predictions.length} Found`}
                      color="secondary"
                      variant="outlined"
                      sx={{ width: "100%", fontWeight: 600 }}
                    />
                  </Grid>
                </Grid>

                {loading ? (
                  <Typography
                    align="center"
                    sx={{ mt: 5, color: "text.secondary" }}
                  >
                    Analyzing data with AI...
                  </Typography>
                ) : predictions.length === 0 ? (
                  <Box sx={{ textAlign: "center", mt: 4, opacity: 0.7 }}>
                    <PersonRemoveIcon
                      sx={{ fontSize: 40, color: "gray", mb: 1 }}
                    />
                    <Typography>
                      No drop-off predictions for this date.
                    </Typography>
                  </Box>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{ borderRadius: 2, borderColor: "#e5e7eb" }}
                  >
                    <List sx={{ p: 0 }}>
                      {/* predictions array par loop chal raha hai */}
                      {predictions.map((patient, index) => (
                        <React.Fragment key={index}>
                          <ListItem
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 1.5,
                            }}
                          >
                            {/* Patient ki Details */}
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                {patient.patient_name ||
                                  `Patient ID: ${patient.ptn_id}`}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Dtl ID: {patient.dtl_id}
                              </Typography>
                            </Box>

                            {/* Action Button */}
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleActionClick(patient)} // Click karne par patient select ho jayega
                            >
                              Add Reason
                            </Button>
                          </ListItem>

                          {/* Har item ke baad ek line (divider) */}
                          {index < predictions.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            )}

            {/* STEP 2: The Reason Form */}
            {activeStep === 2 && selectedPatient && (
              <form onSubmit={handleAiSubmit}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: "rgba(168, 85, 247, 0.05)",
                    borderLeft: "4px solid #a855f7",
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Reviewing:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    {selectedPatient.patient_name ||
                      `Patient ID: ${selectedPatient.ptn_id}`}
                  </Typography>
                </Paper>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Service Status</InputLabel>
                      <Select
                        name="service_status"
                        value={aiForm.service_status}
                        onChange={handleFormChange}
                        label="Service Status"
                      >
                        <MenuItem value={1}>1 - Dropped</MenuItem>
                        <MenuItem value={2}>2 - Follow-up Required</MenuItem>
                        <MenuItem value={3}>3 - Converted / Completed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {parseInt(aiForm.service_status) === 2 && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        type="datetime-local"
                        label="Follow-up Date & Time"
                        name="in_folloup"
                        value={aiForm.in_folloup}
                        onChange={handleFormChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Drop-off Reason</InputLabel>
                      <Select
                        name="reason"
                        value={aiForm.reason}
                        onChange={handleFormChange}
                        label="Drop-off Reason"
                      >
                        {Array.isArray(aiReasons) &&
                          aiReasons.map((r, index) => (
                            <MenuItem
                              key={index}
                              value={r.id || r.reason_id || index + 1}
                            >
                              {r.reason_name ||
                                r.title ||
                                `Reason ${index + 1}`}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Additional Remark"
                      name="Remark"
                      multiline
                      rows={3}
                      value={aiForm.Remark}
                      onChange={handleFormChange}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      endIcon={<SendIcon />}
                      sx={{
                        background:
                          "linear-gradient(135deg, #a855f7 0%, #6366f1 100%)",
                        textTransform: "none",
                        fontWeight: 600,
                        py: 1.2,
                      }}
                    >
                      Submit AI Feedback
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default AIDropoffModal;
